import type { Coin, CurrencyDeep, GetPricesInput, GetPricesOutput } from '@base/types'

/**
 * Finds the exchange office charge with amountTo closest to 1000,
 * considering only charges where amountFrom < 1000.
 * @param charges Array of exchange office charges
 * @returns The charge object or undefined
 */
function exchangeOfficeCharge (
  charges?: { amountFrom?: number, amountTo?: number, profitPlatformPercent?: number }[],
) {
  if (!charges?.length) return undefined
  // Filter charges where amountFrom < 1000
  const filtered = charges.filter(c => (c.amountFrom ?? 0) < 1000)
  if (!filtered.length) return undefined
  return filtered.reduce((prev, curr) =>
    Math.abs((curr.amountTo ?? 0) - 1000) < Math.abs((prev.amountTo ?? 0) - 1000) ? curr : prev,
  )
}

/**
 * Resolves the currenciues by fetching the prices from the CurrencyFeed service using the specified provider.
 * Able to fetch multiple coins at once with the same provider and base currency by using the CurrencyFeed service.
 *
 * @param {CurrencyDeep[]} coins - The currency objects to resolve.
 * @param {Coin} [czkCoin] - The CZK coin object for showingPrice conversion purposes.
 * @return {Promise<Coin[]>} - The resolved currency objects with the price and other properties.
 */
export const resolveCurrenciesToCoins = async (coins: CurrencyDeep[], czkCoin?: Coin): Promise<Coin[]> => {
  type CurrencyMapping = Record<GetPricesInput['provider'], GetPricesInput['currencies']>

  const currencyMappings: CurrencyMapping = {
    CNB: [],
    KRAKEN: [],
    CURRENCYLAYER: [],
    CUSTOM: [],
  }
  const baseCurrency = 'EUR'

  /* Decompose the coins into objects that will be sent to currency feed service */

  coins.forEach(c => {
    if (!c.code) {
      console.error('Coin code is missing, skipping fetch', c)
      return
    }

    if (!c.priceFeed) {
      console.error('Coin provider is missing, skipping fetch', c)
      return
    }

    if (currencyMappings[c.priceFeed]) {
      currencyMappings[c.priceFeed].push(c.code)
    }
    else {
      currencyMappings[c.priceFeed] = [c.code]
    }
  })
  const promises: Promise<GetPricesOutput | null>[] = []

  for (const provider of Object.keys(currencyMappings) as GetPricesInput['provider'][]) {
    if (!currencyMappings[provider]) {
      continue
    }

    if (!currencyMappings[provider].length) {
      continue
    }

    const currencies = currencyMappings[provider]

    const body: GetPricesInput = {
      baseCurrency,
      provider,
      currencies: currencies,
      useCache: true,
      refreshRateInSeconds: 60,
    }

    const promise = $fetch<GetPricesOutput>('/api/prices', {
      method: 'POST',
      body,
    }).catch(error => {
      console.error(`Error fetching data from CurrencyFeed service`, error)
      return null
    })
    promises.push(promise)
  }

  /* Send all requests to the currency feed service */

  let currencyFeedDataArray: Array<GetPricesOutput | null> = []
  try {
    currencyFeedDataArray = await Promise.all(promises)
    currencyFeedDataArray = currencyFeedDataArray.map(dataObj => {
      if (!dataObj?.data.status || dataObj?.data.status.toLowerCase() !== 'ok') {
        console.error(`Error fetching dataObj from CurrencyFeed service: ${dataObj?.message}`)
        return null
      }
      return dataObj
    }).filter(dataObj => dataObj !== null)
  }
  catch (error) {
    console.error('Error fetching CurrencyFeed data', error)
  }

  /* Compose the coins back with the data from the currency feed service */

  const finishedCoins = coins.map(c => {
    const coin: Coin = {
      ...c,
      name: c.label,
      showingPrice: 0,
      price: 0,
      chart: c.chartImg ?? undefined,
    }

    const coinPriceEurMerged = c.pairEur?.replace('-', '') || ''

    const currencyFeedDataObject = currencyFeedDataArray.find(currencyFeedData => currencyFeedData?.data.provider.toLowerCase() === c.priceFeed.toLowerCase())

    const price = currencyFeedDataObject?.data.quotes[coinPriceEurMerged]
    if (coin.priceFeed === 'KRAKEN') {
      const absoluteChange24H = currencyFeedDataObject?.data.quotesDailyChange?.[coinPriceEurMerged]
      if (absoluteChange24H && price) coin.change24HPercent = absoluteChange24H / price * 100
    }

    if (!price)
      console.error(`Price for pair ${coinPriceEurMerged} not found.`)

    coin.price = price ?? 0

    if (c.showingPrice) {
      coin.showingPrice = c.showingPrice
    }
    else if (czkCoin) {
      const czkDefaultCharge = czkCoin!.charges?.find(c => c.isDefault)
      const czkExchangeOfficeCharge = exchangeOfficeCharge(czkCoin.exchangeOfficeCharges)

      const profitPlatformPercent = czkExchangeOfficeCharge?.profitPlatformPercent ?? czkDefaultCharge?.profitPlatformPercent ?? 0
      const profitBitbeliPercent = czkDefaultCharge?.platform.profitBitbeliPercent ?? 0

      const charge = roundMath((100 - profitPlatformPercent - profitBitbeliPercent) / 100, 2)
      coin.showingPrice = coin.price / czkCoin!.price / charge
    }

    return coin
  })

  return finishedCoins
}
